import type { SegmentRule, VoterCounts, VoterDataFieldDto, VoterDataOptionDto } from '@signalvote/shared';
import { VoterDataFieldKind, assertNever } from '@signalvote/shared';

/**
 * The A4 filter-state model, shared by the sentence and the field popover. One model everywhere:
 * checked = in. A field with no rule is Everyone (every option checked); a rule lists the option
 * labels kept, in catalog order. A vote-history field has no options: its rule says whether they
 * voted, and no rule is Any.
 */

/** A voter share of no data this large earns the "Includes / Leaves out" note. */
const MISSING_NOTE_THRESHOLD = 0.05;

/** Whether a field is offered as Any · one · the other rather than as checkboxes. */
export function isYesNo(field: VoterDataFieldDto): boolean {
  switch (field.kind) {
    case VoterDataFieldKind.Flag:
    case VoterDataFieldKind.Vote:
      return true;
    case VoterDataFieldKind.Enum:
      return false;
    default:
      return assertNever(field.kind);
  }
}

export const ruleFor = (rules: SegmentRule[], fieldId: string) => rules.find((r) => r.fieldId === fieldId);

/** The option labels a field keeps: its rule's values, or every option when it has none. */
export function keptOf(field: VoterDataFieldDto, rules: SegmentRule[]): string[] {
  const rule = ruleFor(rules, field.id);
  return rule?.kind === 'Values' ? rule.values : field.options.map((o) => o.label);
}

/** Whether a vote field's rule keeps those who voted (true) or didn't (false); null is Any. */
export function votedOf(field: VoterDataFieldDto, rules: SegmentRule[]): boolean | null {
  const rule = ruleFor(rules, field.id);
  return rule?.kind === 'Voted' ? rule.voted : null;
}

export const isNarrowed = (field: VoterDataFieldDto, rules: SegmentRule[]) => ruleFor(rules, field.id) !== undefined;

/** "Everyone", or how far a field is narrowed. */
export function statusOf(field: VoterDataFieldDto, rules: SegmentRule[]): string {
  if (!isNarrowed(field, rules)) return 'Everyone';
  const kept = keptOf(field, rules);
  switch (field.kind) {
    case VoterDataFieldKind.Flag:
      return kept[0];
    case VoterDataFieldKind.Enum:
      return `Only ${kept.length} of ${field.options.length}`;
    case VoterDataFieldKind.Vote:
      return votedOf(field, rules) ? 'Voted' : 'Didn’t vote';
    default:
      return assertNever(field.kind);
  }
}

/**
 * The rules after keeping `kept` for `field`. Keeping every option removes the rule (Everyone);
 * anything else replaces or adds it, in catalog order, with a fresh id from `newId`.
 */
export function withKept(rules: SegmentRule[], field: VoterDataFieldDto, kept: string[], newId: () => string): SegmentRule[] {
  const values = field.options.map((o) => o.label).filter((label) => kept.includes(label));
  const existing = ruleFor(rules, field.id);
  if (values.length === field.options.length) return rules.filter((r) => r !== existing);
  return upsert(rules, existing, { id: existing?.id ?? newId(), fieldId: field.id, kind: 'Values', values });
}

/** The rules after a vote field is set to voted, didn't vote, or Any (null, no rule). */
export function withVoted(rules: SegmentRule[], field: VoterDataFieldDto, voted: boolean | null, newId: () => string): SegmentRule[] {
  const existing = ruleFor(rules, field.id);
  if (voted === null) return rules.filter((r) => r !== existing);
  return upsert(rules, existing, { id: existing?.id ?? newId(), fieldId: field.id, kind: 'Voted', voted });
}

/** `rule` in place of `existing`, or added at the end when the field had none. */
function upsert(rules: SegmentRule[], existing: SegmentRule | undefined, rule: SegmentRule): SegmentRule[] {
  return existing ? rules.map((r) => (r === existing ? rule : r)) : [...rules, rule];
}

/**
 * `kept` with `label`'s box toggled, or null when that would uncheck the last box: a field can't
 * keep nobody, so the last box refuses and "only" is the way to pick a single value.
 */
export function toggledKept(kept: string[], label: string): string[] | null {
  if (!kept.includes(label)) return [...kept, label];
  return kept.length === 1 ? null : kept.filter((l) => l !== label);
}

/** A field's counts, measured like a breakdown: without the field's own rule. Undefined where not known yet. */
export interface FieldCounts {
  /** Everyone the field's options split: Any. */
  base: number | undefined;
  options: Record<string, number | undefined>;
}

/**
 * A field's counts. A field with no rule is counted under the whole segment, so the list's own
 * counts hold them. A narrowed field is measured without its rule: `without`, the counts of the
 * segment without it, asked for when its popover opens. Until then only the options it keeps are
 * known, from the whole segment.
 */
export function fieldCountsOf(
  field: VoterDataFieldDto,
  rules: SegmentRule[],
  counts: VoterCounts | undefined,
  without: VoterCounts | undefined,
): FieldCounts {
  if (!isNarrowed(field, rules)) return { base: counts?.total, options: counts?.breakdowns[field.id] ?? {} };
  if (without) return { base: without.total, options: without.breakdowns[field.id] ?? {} };
  const whole = counts?.breakdowns[field.id] ?? {};
  return { base: undefined, options: Object.fromEntries(keptOf(field, rules).map((label) => [label, whole[label]])) };
}

/**
 * The share of the base with no data for this field: voters no option covers. Rounded to a whole
 * percent, or null when it's too small to mention or not known yet.
 */
export function missingPercent(field: VoterDataFieldDto, counts: FieldCounts): number | null {
  const { base, options } = counts;
  if (!base || field.options.some((o) => options[o.label] === undefined)) return null;
  const covered = field.options.reduce((sum, o) => sum + options[o.label]!, 0);
  const share = (base - covered) / base;
  return share > MISSING_NOTE_THRESHOLD ? Math.round(share * 100) : null;
}

/** "a", "a or b", "a, b or c". */
function orList(words: string[]): string {
  return words.length < 3 ? words.join(' or ') : `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`;
}

/** A run of consecutive age bands as "18 to 34" or "55 or older", or null when they don't join up. */
function ageSpan(field: VoterDataFieldDto, kept: VoterDataOptionDto[]): string | null {
  if (kept.length === 0 || kept.some((o) => o.ageFrom == null)) return null;
  const indexes = kept.map((o) => field.options.indexOf(o));
  if (indexes.some((index, i) => i > 0 && index !== indexes[i - 1] + 1)) return null;
  const from = kept[0].ageFrom!;
  const to = kept[kept.length - 1].ageTo;
  return to == null ? `${from} or older` : `${from} to ${to}`;
}

/**
 * How a field's rule reads in the sentence: "are not Republican", "are 55 or older", "have a cell
 * phone", "voted in the 2024 general".
 */
export function phraseOf(field: VoterDataFieldDto, rules: SegmentRule[]): string {
  switch (field.kind) {
    case VoterDataFieldKind.Vote:
      return votePhrase(field, votedOf(field, rules));
    case VoterDataFieldKind.Flag:
    case VoterDataFieldKind.Enum:
      return valuesPhrase(field, keptOf(field, rules));
    default:
      return assertNever(field.kind);
  }
}

function votePhrase(field: VoterDataFieldDto, voted: boolean | null): string {
  // No rule is Any: a field only reads this way while its popover is open.
  if (voted === null) return `voted or not in ${field.election}`;
  return `${voted ? 'voted' : 'didn’t vote'} in ${field.election}`;
}

/** How keeping `keptLabels` of a field with options reads: "are women", "have a cell phone". */
export function valuesPhrase(field: VoterDataFieldDto, keptLabels: string[]): string {
  const kept = field.options.filter((o) => keptLabels.includes(o.label));
  const word = (o: VoterDataOptionDto) => o.phrase ?? o.label;
  // Every option kept is Everyone: a field only reads this way while its popover is open.
  if (kept.length === field.options.length) return `any ${field.label.toLowerCase()}`;
  if (isYesNo(field)) return word(kept[0]);

  const span = ageSpan(field, kept);
  if (span) return `${field.verb} ${span}${field.suffix}`;
  const left = field.options.filter((o) => !kept.includes(o));
  if (left.length === 1 && kept.length >= 2) return `${field.negatedVerb} ${word(left[0])}${field.suffix}`;
  return `${field.verb} ${orList(kept.map(word))}${field.suffix}`;
}
